import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  catchError,
  defaultIfEmpty,
  defer,
  finalize,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoginResponse, RegisterRequest, User } from '../models/user.model';
import { TokenResponse, TokenService } from './token.service';

/**
 * Endpoint de Passport al que pegamos: `${apiUrl}/oauth/token`.
 *
 * IMPORTANTE: Passport instala las rutas en DOS prefijos distintos:
 *  - `/oauth/*` (raíz, vía `Passport::routes()` del AuthServiceProvider) —
 *    queda FUERA del middleware CORS de Laravel (`config/cors.php` sólo
 *    cubre `api/*`). Si le pegamos ahí desde el browser, el preflight falla.
 *  - `/api/oauth/*` (explícita en `routes/api.php`) — SÍ pasa CORS.
 * Por eso usamos siempre `apiUrl` + `/oauth/token`.
 */
const TOKEN_URL = `${environment.apiUrl}/oauth/token`;

/**
 * Servicio de autenticación. Abarca dos flujos OAuth (Laravel Passport):
 *
 *  1. **client_credentials** (tienda pública): obtiene un token "de aplicación"
 *     que se adjunta automáticamente a todas las requests sin sesión de
 *     usuario. Lo gestionan `getClientCredentials()` y `ensureClientCredentials()`.
 *
 *  2. **password grant** (cuenta de usuario): `login()` cambia email+password
 *     por access+refresh tokens. `me()` restaura la sesión leyendo
 *     `identify-user`. `logout()` limpia todo.
 *
 * Expone signals reactivos (`currentUser`, `isLoading`, `isAuthenticated`)
 * para que componentes lean el estado de sesión sin suscribirse a observables.
 * El almacenamiento real de tokens lo delega en `TokenService`.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly tokenService = inject(TokenService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  // Estado de sesión expuesto como signals (no se escriben desde fuera).
  private readonly _currentUser = signal<User | null>(null);
  private readonly _isLoading = signal(false);

  /** Usuario autenticado actual (null si no hay sesión). */
  readonly currentUser = this._currentUser.asReadonly();
  /** True mientras se está restaurando la sesión desde el backend. */
  readonly isLoading = this._isLoading.asReadonly();
  /** Derivado: hay usuario → hay sesión. */
  readonly isAuthenticated = computed(() => !!this._currentUser());

  /** Sube con cada `login()`/`logout()`: un `me()` solo escribe si sigue en la suya. */
  private sessionGeneration = 0;
  /** `me()` en vuelo de la sesión actual; `isLoading` baja cuando llega a 0. */
  private pendingMe = 0;
  private readonly sessionChange$ = new Subject<void>();

  // ── Client credentials (public endpoints) ─────────────────────────

  /**
   * POST `/api/oauth/token` con `grant_type=client_credentials`.
   * Guarda el token cliente vía `TokenService.setClientToken` (localStorage en
   * browser, almacén de proceso en SSR). En SSR las peticiones concurrentes de
   * todo el proceso se funden en un solo POST (`shareServerClientTokenRequest`).
   */
  getClientCredentials(): Observable<TokenResponse> {
    const body = {
      grant_type: 'client_credentials',
      client_id: environment.passportClientId,
      client_secret: environment.passportClientSecret,
    };
    const request$ = this.http.post<TokenResponse>(TOKEN_URL, body).pipe(
      tap((res) => this.tokenService.setClientToken(res)),
    );
    return this.isBrowser ? request$ : this.tokenService.shareServerClientTokenRequest(request$);
  }

  /**
   * Si ya hay un token cliente no-expirado localmente, lo reutiliza. Si no,
   * pide uno nuevo. Nunca lanza: en caso de fallo devuelve `of(false)` para
   * que `APP_INITIALIZER` no bloquee el arranque de la app.
   *
   * Nota: `hasValidClientToken()` sólo verifica el `expiresAt` guardado
   * (localStorage o almacén de proceso en SSR); no valida contra el backend.
   * Si el backend revoca el token
   * (reinicio de Passport, cambio de environment, etc.) la request siguiente
   * dará 401 y el `authInterceptor` lo renegocia automáticamente.
   */
  ensureClientCredentials(): Observable<boolean> {
    if (this.tokenService.hasValidClientToken()) {
      return of(true);
    }
    return this.getClientCredentials().pipe(
      switchMap(() => of(true)),
      catchError((err) => {
        console.error('[auth] falló obtener client_credentials', err);
        return of(false);
      }),
    );
  }

  // ── User auth (password grant) ────────────────────────────────────

  /**
   * POST `/api/oauth/token` con `grant_type=password`. Cambia email+password
   * por access_token + refresh_token y los guarda en localStorage como
   * tokens de usuario (distintos del cliente). El caller típicamente hace
   * `.pipe(switchMap(() => me()))` para cargar el perfil.
   */
  login(email: string, password: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(TOKEN_URL, {
        grant_type: 'password',
        client_id: environment.passportClientId,
        client_secret: environment.passportClientSecret,
        username: email,
        password,
      })
      .pipe(
        tap((res) => {
          this.tokenService.setUserTokens(res.access_token, res.refresh_token);
          this.startSession();
        }),
      );
  }

  /** POST `/api/new-web-user` para registrar un cliente nuevo en la tienda. */
  register(data: RegisterRequest): Observable<User> {
    return this.http.post<User>(`${environment.apiUrl}/new-web-user`, data);
  }

  /** Limpia tokens de usuario, borra el signal `currentUser` y redirige a `/`. */
  logout(): void {
    this.tokenService.clearUserTokens();
    this.startSession();
    this._currentUser.set(null);
    this.router.navigate(['/']);
  }

  /**
   * Si hay token de usuario en localStorage, pega GET `/api/identify-user`
   * para cargar el perfil. Si falla (token revocado/expirado), limpia tokens
   * y deja `currentUser = null`. Si no hay token, devuelve `of(null)` sin
   * hacer request.
   *
   * **Solo escribe la sesión que inició la petición** (Astra W1 H1): una
   * respuesta que vuelve cuando ya hay otra sesión —se salió, entró otra cuenta
   * o el token cambió en otra pestaña— no toca `currentUser` ni los tokens y
   * devuelve `null`. Sin esto, la recuperación de A tras un 401 reponía a A
   * dentro de la sesión de B (200) o le borraba los tokens a B (401). Además,
   * `logout()` y `login()` cancelan los `me()` en vuelo de la sesión anterior.
   */
  me(): Observable<User | null> {
    return defer(() => {
      const token = this.tokenService.getUserToken();
      if (!token) return of(null);

      const generation = this.sessionGeneration;
      const isCurrent = () =>
        generation === this.sessionGeneration && this.tokenService.getUserToken() === token;

      this.pendingMe++;
      this._isLoading.set(true);
      return this.http.get<User>(`${environment.apiUrl}/identify-user`).pipe(
        map((user) => {
          if (!isCurrent()) return null;
          this._currentUser.set(user);
          return user;
        }),
        catchError(() => {
          if (isCurrent()) {
            this.tokenService.clearUserTokens();
            this._currentUser.set(null);
          }
          return of(null);
        }),
        takeUntil(this.sessionChange$),
        defaultIfEmpty(null),
        finalize(() => {
          // Una petición de una sesión anterior no descuenta: su cuenta se reinició con la sesión nueva.
          if (generation !== this.sessionGeneration) return;
          this.pendingMe = Math.max(0, this.pendingMe - 1);
          if (this.pendingMe === 0) this._isLoading.set(false);
        }),
      );
    });
  }

  /**
   * Fusiona `patch` en `currentUser` tras un guardado propio (B20 de B10: datos personales y correo en
   * `/perfil/ajustes`), sin volver a pedir `identify-user`. **Solo si la sesión sigue siendo de `userId`**: la
   * respuesta de un guardado de A que llega con B dentro (o sin sesión) no toca nada.
   */
  patchCurrentUser(userId: number, patch: Partial<User>): void {
    const user = this._currentUser();
    if (user?.id !== userId) return;
    // El id no se parchea: un `patch` con otro id convertiría la sesión de A en la de otra cuenta.
    this._currentUser.set({ ...user, ...patch, id: user.id });
  }

  /**
   * Empieza una sesión nueva (login o logout): los `me()` de la anterior se
   * cancelan, dejan de contar para `isLoading` y, si alguno respondiera, no
   * escribe nada.
   */
  private startSession(): void {
    this.sessionGeneration++;
    this.pendingMe = 0;
    this._isLoading.set(false);
    this.sessionChange$.next();
  }

  /** GET `/api/restore-password?email=` — envía mail con token de reset. */
  forgotPassword(email: string): Observable<unknown> {
    const params = new HttpParams().set('email', email);
    return this.http.get(`${environment.apiUrl}/restore-password`, { params });
  }

  /** GET `/api/check-restore-password-token?password_reset_token=` — valida el token antes de pedir nueva password. */
  checkResetToken(token: string): Observable<unknown> {
    const params = new HttpParams().set('password_reset_token', token);
    return this.http.get(`${environment.apiUrl}/check-restore-password-token`, {
      params,
    });
  }

  /**
   * PUT `/api/new-password/{userId}` — fija la password nueva usando el token de reset.
   * El backend valida `confirmed` sobre `password`, por lo que requiere
   * `password_confirmation` en el body además de la nueva contraseña y el
   * token (`password_reset_token`).
   */
  resetPassword(
    userId: number,
    password: string,
    passwordConfirmation: string,
    token: string,
  ): Observable<unknown> {
    return this.http.put(`${environment.apiUrl}/new-password/${userId}`, {
      password,
      password_confirmation: passwordConfirmation,
      password_reset_token: token,
    });
  }

  /**
   * PUT `/api/email-verification/{userId}` con `{ verification_token }`.
   * Lo consume la ruta `/auth/email-verification/:id/:token` (llega desde el
   * correo de bienvenida) para activar la cuenta.
   */
  verifyEmail(userId: number, token: string): Observable<unknown> {
    return this.http.put(`${environment.apiUrl}/email-verification/${userId}`, {
      verification_token: token,
    });
  }

  /** GET `/api/resend-verification-email/{userId}` — reenvía el correo de verificación. */
  resendVerificationEmail(userId: number): Observable<unknown> {
    return this.http.get(
      `${environment.apiUrl}/resend-verification-email/${userId}`,
    );
  }

  /**
   * Bootstrap de sesión. Lo llama el `APP_INITIALIZER`:
   *  1) garantiza que exista un token cliente válido,
   *  2) si había token de usuario guardado, carga su perfil.
   * Con esto, al arrancar la app ya tenemos todo listo para pegar a
   * endpoints públicos y para mostrar al usuario si ya estaba logueado.
   */
  initAuth(): Observable<User | null> {
    // En SSR pedimos el client token (queda cacheado a nivel de proceso y
    // habilita fetches server-side autenticados), pero NO
    // llamamos a me(): esa restauración de sesión depende del access token
    // de usuario, que vive en localStorage y por tanto es browser-only.
    if (!this.isBrowser) {
      return this.ensureClientCredentials().pipe(switchMap(() => of(null)));
    }
    return this.ensureClientCredentials().pipe(switchMap(() => this.me()));
  }
}
